import { StandardEvents } from '@shopify/events';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Brain Ritual's canonical PDP uses a custom plan selector and a guarded
 * subscription form, so Horizon's generic sticky-add-to-cart component cannot
 * own this path. This component is deliberately a UI proxy: it mirrors the
 * selected plan and activates the canonical form button instead of issuing its
 * own cart request. Variant, selling-plan and purchase-product truth therefore
 * stay in the one native Horizon product form.
 */
class BrPdpStickyCta extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {IntersectionObserver | null} */
  #mainCtaObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {MutationObserver | null} */
  #mainCtaMutationObserver = null;

  /** @type {ResizeObserver | null} */
  #barResizeObserver = null;

  /** @type {HTMLElement | null} */
  #root = null;

  /** @type {HTMLElement | null} */
  #bar = null;

  /** @type {HTMLButtonElement | null} */
  #mainButton = null;

  /** @type {HTMLButtonElement | null} */
  #stickyButton = null;

  /** @type {HTMLInputElement | null} */
  #mainAck = null;

  /** @type {HTMLInputElement | null} */
  #stickyAck = null;

  /** @type {HTMLElement | null} */
  #pageWrapper = null;

  #passedMainCta = false;
  #footerNear = false;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.#passedMainCta = false;
    this.#footerNear = false;

    this.#root = this.closest('[data-br-canonical-pdp]');
    this.#bar = this.querySelector('[data-sticky-bar]');
    this.#mainButton = this.#root?.querySelector('[ref="addToCartButton"]') ?? null;
    this.#stickyButton = this.querySelector('[data-sticky-button]');
    this.#mainAck = this.#root?.querySelector('[data-ack-checkbox]') ?? null;
    this.#stickyAck = this.querySelector('[data-sticky-ack]');
    this.#pageWrapper = document.querySelector('.page-wrapper');

    if (!this.#root || !this.#bar || !this.#mainButton || !this.#stickyButton) return;

    const { signal } = this.#abortController;

    this.#root.querySelectorAll('.br-pdp-main__plan-input').forEach((input) => {
      input.addEventListener('change', this.#queueStateSync, { signal });
    });
    this.#mainAck?.addEventListener('change', this.#queueStateSync, { signal });
    this.#stickyAck?.addEventListener('change', this.#handleStickyAckChange, { signal });
    this.#stickyButton.addEventListener('click', this.#handleStickyClick, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartSettled, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartSettled, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollContainerChange, {
      signal,
    });

    // The purchase-options controller updates the real button's availability
    // and label. Mirror both changes rather than rebuilding that state here.
    this.#mainCtaMutationObserver = new MutationObserver(this.#queueStateSync);
    this.#mainCtaMutationObserver.observe(this.#mainButton, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
    });

    this.#barResizeObserver = new ResizeObserver(this.#syncDockOffset);
    this.#barResizeObserver.observe(this.#bar);

    this.#setupVisibilityObservers();
    this.hidden = false;
    this.#bar.inert = true;
    this.#syncState();
  }

  disconnectedCallback() {
    this.#mainCtaObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#mainCtaMutationObserver?.disconnect();
    this.#barResizeObserver?.disconnect();
    this.#abortController.abort();
    this.#pageWrapper?.style.removeProperty('--br-pdp-sticky-offset');
    if (this.#mainButton) this.#mainButton.dataset.puppet = 'false';
  }

  #setupVisibilityObservers() {
    if (!this.#mainButton) return;

    // Horizon scrolls inside .page-wrapper at >=990px and on the document
    // below that breakpoint. The shared helper returns the element root for
    // desktop and null (the viewport) for tablet/mobile.
    const intersectionRoot = getIntersectionRoot();

    this.#mainCtaObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const rootTop = entry.rootBounds?.top ?? 0;
        this.#passedMainCta = !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
        this.#syncVisibility();
      },
      { root: intersectionRoot }
    );
    this.#mainCtaObserver.observe(this.#mainButton);

    const footer =
      document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#footerNear = entry.isIntersecting;
        this.#syncVisibility();
      },
      {
        root: intersectionRoot,
        // Retire the dock before it can cover footer navigation or legal links.
        rootMargin: '0px 0px 120px 0px',
      }
    );
    this.#footerObserver.observe(footer);
  }

  #queueStateSync = () => {
    queueMicrotask(() => this.#syncState());
  };

  #syncState() {
    if (!this.#root || !this.#mainButton || !this.#stickyButton) return;

    const selectedPlan = this.#root
      .querySelector('.br-pdp-main__plan-input:checked')
      ?.closest('.br-pdp-main__plan');
    if (!(selectedPlan instanceof HTMLElement)) return;

    const planName =
      selectedPlan.querySelector('.br-pdp-main__plan-name')?.textContent?.trim() ?? '';
    const price =
      selectedPlan.querySelector('.br-pdp-main__offer-billing')?.textContent?.trim() ||
      selectedPlan.querySelector('.br-pdp-main__plan-amount')?.textContent?.trim() ||
      '';
    const compare =
      selectedPlan.querySelector('.br-pdp-main__plan-compare')?.textContent?.trim() ?? '';
    const ctaLabel =
      this.#mainButton
        .querySelector('.add-to-cart-text__content > span > span')
        ?.textContent?.trim() ||
      this.#mainButton.textContent?.trim() ||
      '';
    const isSubscription = selectedPlan.dataset.subscription !== 'false';

    const planElement = this.querySelector('[data-sticky-plan]');
    const priceElement = this.querySelector('[data-sticky-price]');
    const compareElement = this.querySelector('[data-sticky-compare]');
    const ctaElement = this.querySelector('[data-sticky-cta-label]');
    const ackRow = this.querySelector('[data-sticky-ack-row]');

    if (planElement) planElement.textContent = planName;
    if (priceElement) priceElement.textContent = price;
    if (compareElement) {
      compareElement.textContent = compare;
      compareElement.toggleAttribute('hidden', compare === '');
    }
    if (ctaElement) ctaElement.textContent = ctaLabel;

    ackRow?.classList.toggle('is-hidden', !isSubscription);
    if (this.#stickyAck && this.#mainAck) {
      this.#stickyAck.checked = this.#mainAck.checked;
      this.#stickyAck.required = isSubscription;
    }

    this.dataset.subscription = String(isSubscription);
    this.dataset.variantId = selectedPlan.dataset.variantId || '';
    this.dataset.sellingPlanId = isSubscription ? selectedPlan.dataset.sellingPlanId || '' : '';
    this.#stickyButton.disabled = this.#mainButton.disabled;
  }

  #syncVisibility() {
    if (!this.#bar) return;

    const visible = this.#passedMainCta && !this.#footerNear;
    this.dataset.visible = String(visible);
    this.#bar.inert = !visible;
    this.#bar.setAttribute('aria-hidden', String(!visible));
    this.#syncDockOffset();
  }

  #syncDockOffset = () => {
    if (!this.#bar || !this.#pageWrapper) return;
    const visible = this.dataset.visible === 'true';
    this.#pageWrapper.style.setProperty(
      '--br-pdp-sticky-offset',
      visible ? `${this.#bar.getBoundingClientRect().height}px` : '0px'
    );
  };

  #handleScrollContainerChange = () => {
    this.#mainCtaObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#passedMainCta = false;
    this.#footerNear = false;
    this.#syncVisibility();
    this.#setupVisibilityObservers();
  };

  #handleStickyAckChange = () => {
    if (!this.#stickyAck || !this.#mainAck) return;
    this.#mainAck.checked = this.#stickyAck.checked;
    this.#mainAck.dispatchEvent(new Event('change', { bubbles: true }));
    this.#queueStateSync();
  };

  #handleStickyClick = () => {
    if (!this.#mainButton || !this.#stickyButton || this.#stickyButton.disabled) return;
    this.#mainButton.dataset.puppet = 'true';
    this.#mainButton.click();
  };

  #handleCartSettled = (event) => {
    const resetPuppet = () => {
      if (this.#mainButton) this.#mainButton.dataset.puppet = 'false';
      this.#queueStateSync();
    };

    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };
}

if (!customElements.get('br-pdp-sticky-cta')) {
  customElements.define('br-pdp-sticky-cta', BrPdpStickyCta);
}
